import { useState, useRef, useEffect, useContext } from 'react';
import accountCtx from 'context/account';
import createStore from 'ctx-provider';
import {
	getScope,
	ping,
	logout as apiLogout,
	setUnauthorizedHandler,
	getPasskeyAuthOptions,
	verifyPasskeyAuth,
} from 'api/trading-api';
import { startAuthentication } from '@simplewebauthn/browser';
import { later } from 'lib/timers';
import useAlert from 'hooks/useAlert';
import { LOGOUT_AFTER_IDDLE, LOGOUT_AFTER_IDDLE_MIN } from 'config/constants';

const KEEP_ALIVE_MINS = 1;
const PING_TIME_MS = 1000 * 60 * KEEP_ALIVE_MINS;
const LOGOUT_AFTER_IDDLE_MS = 1000 * 60 * LOGOUT_AFTER_IDDLE_MIN;

const useAuthentication = () => {
	const { startPolling, stopPolling } = useContext(accountCtx);
	const alert = useAlert();
	const [scope, setScope] = useState();
	const [initialized, setInitialized] = useState(false);
	const timer = useRef();
	const lastActivityRef = useRef(Date.now());
	const handleActivity = useRef();

	// pointerdown is listed because a touch screen reports a tap as pointerdown +
	// pointerup without a pointermove in between, so on a phone the other two
	// events fire rarely and the user reads as idle while actively using the app.
	const startActivityListeners = () => {
		document.addEventListener('keydown', handleActivity.current);
		document.addEventListener('pointermove', handleActivity.current);
		document.addEventListener('pointerdown', handleActivity.current);
	};

	const stopActivityListeners = () => {
		document.removeEventListener('keydown', handleActivity.current);
		document.removeEventListener('pointermove', handleActivity.current);
		document.removeEventListener('pointerdown', handleActivity.current);
	};

	const startHealthCheck = () => {
		if (LOGOUT_AFTER_IDDLE) {
			timer.current = later(
				async () => {
					const iddleMs = Date.now() - lastActivityRef.current;
					if (iddleMs >= LOGOUT_AFTER_IDDLE_MS) {
						// TODO: fix the error that is thrown after logout
						console.debug('going to logout', {
							lastActivityRef_current: lastActivityRef.current,
							date_now: Date.now(),
							iddleMs,
							LOGOUT_AFTER_IDDLE_MS,
						});
						console.debug('LOGOUT fase A');
						logout();
					} else {
						const latestScope = await ping();
						if (!latestScope) {
							console.debug('NO latestScope');
							// logout();
						} else if (latestScope !== scope) {
							setScope(latestScope);
						}
					}
				},
				PING_TIME_MS,
				true,
			);
			startActivityListeners();
		}
	};

	const stopHealthCheck = () => {
		if (LOGOUT_AFTER_IDDLE) {
			if (timer.current) {
				timer.current.cancel();
			}
			stopActivityListeners();
		}
	};

	const logout = () => {
		// Unregister first so the logout request itself (which may also 401 if the
		// cookie is already gone) cannot re-trigger the session-expired flow.
		setUnauthorizedHandler(null);
		setScope(null);
		stopHealthCheck();
		// Without this the account poller keeps hitting the API every minute from
		// the login screen, for as long as the page stays open.
		stopPolling();
		apiLogout();
	};

	// Shared post-authentication setup: whether login was via password or passkey,
	// once we have a scope we set it, start the keep-alive health check and account polling.
	const establishSession = latestScope => {
		// Logging in is activity. The idle countdown has to start here, not at
		// whenever this page happened to be loaded: on a mobile PWA the page can
		// sit around for hours, which would otherwise make the first health check
		// log the user straight back out, one minute after signing in.
		lastActivityRef.current = Date.now();
		setScope(latestScope);
		startHealthCheck();
		startPolling();
	};

	const tryLogin = async (account, password) => {
		let success;
		try {
			const latestScope = await getScope({ account, password });
			if (latestScope) {
				success = true;
				establishSession(latestScope);
			}
		} catch (err) {
			console.error(err);
			if (err?.response?.status === 401) {
				alert(err?.response?.data?.error || 'Wrong credentials');
			} else {
				console.error(err);
			}
		}
		return !!success;
	};

	// Usernameless passkey login: get options + nonce, run the WebAuthn ceremony
	// (Face ID / Touch ID on mobile), then verify and establish the same session.
	const tryLoginPasskey = async () => {
		let success;
		try {
			const optionsResponse = await getPasskeyAuthOptions();
			if (!optionsResponse) {
				return false;
			}
			const { options, nonce } = optionsResponse;
			const credential = await startAuthentication(options);
			const latestScope = await verifyPasskeyAuth({ credential, nonce });
			if (latestScope) {
				success = true;
				establishSession(latestScope);
			} else {
				alert('Passkey sign-in failed. Please try again or use your password.');
			}
		} catch (err) {
			// NotAllowedError / AbortError = user cancelled or no passkey offered: stay quiet.
			if (err?.name !== 'NotAllowedError' && err?.name !== 'AbortError') {
				console.error(err);
				alert('Passkey sign-in failed. Please try again or use your password.');
			}
		}
		return !!success;
	};

	const initializeScope = async () => {
		try {
			const latestScope = await ping();
			if (latestScope) {
				establishSession(latestScope);
			}
		} catch (err) {
			console.error('Failed to initialize scope:', err);
		}
		setInitialized(true);
	};

	useEffect(() => {
		handleActivity.current = () => {
			lastActivityRef.current = Date.now();
		};
		initializeScope();

		return () => {
			stopHealthCheck();
			stopPolling();
		};
	}, []);

	// Only arm the session-expired handler while actually logged in, so a 401
	// from a wrong-credentials login or the initial (logged-out) ping does not
	// surface a spurious "session expired" alert.
	useEffect(() => {
		if (scope) {
			setUnauthorizedHandler(() => {
				alert('Your session has expired. Please log in again.', 'warning');
				logout();
			});
		} else {
			setUnauthorizedHandler(null);
		}
	}, [scope]);

	return {
		initialized,
		isLoggedIn: !!scope,
		scope,
		logout,
		tryLogin,
		tryLoginPasskey,
	};
};

const store = createStore(useAuthentication);

export const { Provider } = store;
export default store.ctx;
